import { Component, OnInit, ChangeDetectionStrategy, ChangeDetectorRef, OnDestroy,
  ElementRef, ViewChild, TemplateRef } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';

import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';

import { Observable, Subject, combineLatest, merge } from 'rxjs';
import { map, takeUntil, filter } from 'rxjs/operators';

import { AppState, Selectors, Actions as RootActions } from 'store/root-reducer';
import { CustomerActionTypes } from 'store/actions/customer.actions';
import { RecordActionTypes } from 'store/actions/record.action';
import { SearchActionTypes } from 'store/actions/search.actions';

import { Customer } from 'modules/shared/models/customer.models';
import { Crp } from 'modules/shared/models/crp.models';
import { PatchCustomerPayload } from 'store/models/customer.models';
import { IndividualRecord, CorporateRecord } from 'modules/shared/models/record.models';
import { RecordOtherDataResponse, GetRecordPayload } from 'store/models/record.models';
import { PostScreeningRequest } from 'store/models/screening.models';
import { CrpResponse } from 'store/models/crp.models';
import { CheckBalancePayload } from 'store/models/billable.models';

import * as CustomerActions from 'store/actions/customer.actions';

import { IBreadcrumbs } from 'modules/shared/components/breadcrumbs/breadcrumbs.component';

import { SnackbarService } from 'app/services/snackbar.service';

import { environment } from 'environments/environment';

import { CustomersBase } from '../../customers-base';

@Component({
  selector: 'artemis-customer-overview-page',
  templateUrl: './customer-overview.page.html',
  styleUrls: ['./customer-overview.page.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class CustomerOverviewPage extends CustomersBase implements OnInit, OnDestroy {

  @ViewChild('customerDocument', { static: true }) customerDocument: ElementRef;
  @ViewChild('requireUpdateForm', {static: false}) requireUpdateForm: TemplateRef<any>;

  private requireUpdateFormRef: MatDialogRef<TemplateRef<any>>;

  private unsubscribe$: Subject<void> = new Subject();

  customerID: number;
  customer: { record: IndividualRecord | CorporateRecord, customer: Customer };

  otherData: RecordOtherDataResponse;

  breadcrumbs: Observable<Array<IBreadcrumbs>> = this.store.select(Selectors.customer.getCustomer)
    .pipe(
      takeUntil(this.unsubscribe$),
      map((customer: Customer) => {
        if (customer) {
          return [
            {
              url: '/customers',
              label: 'Customer List'
            }, {
              url: `/customers/${customer.id}`,
              label: customer.name
            }
          ];
        } else {
          return [];
        }
      })
    );

  loading$: Observable<boolean>;

  enableAresIntegration: boolean = environment['ENABLE_ARES_INTEGRATION'];

  requireUpdateContent: string;

  constructor(private activatedRoute: ActivatedRoute,
              private router: Router,
              private cdRef: ChangeDetectorRef,
              private store: Store<AppState>,
              private actions$: Actions,
              private dialog: MatDialog,
              private snackbar: SnackbarService) {
    super();
  }

  ngOnInit() {
    this.getLoadingStatus();
    this.getRequireUpdateActionStatus();

    this.customerID = this.activatedRoute.snapshot.params['id'];
    this.initCustomer();
  }

  /**
   * Get loading status from store
   * @method getLoadingStatus
   */
  private getLoadingStatus(): void {
    this.loading$ = combineLatest([
      this.store.select(Selectors.customer.getLoading),
      this.store.select(Selectors.record.getLoading),
      this.store.select(Selectors.search.getLoading)
    ]).pipe(
      takeUntil(this.unsubscribe$),
      map(([customerLoading, recordLoading, screeningLoading]:
           [Array<CustomerActionTypes>, Array<RecordActionTypes>, Array<SearchActionTypes>]) => {
        return customerLoading.includes(CustomerActionTypes.GET_CUSTOMER) ||
               customerLoading.includes(CustomerActionTypes.PATCH_CUSTOMER) ||
               recordLoading.includes(RecordActionTypes.GET_OTHER_DATA) ||
               recordLoading.includes(RecordActionTypes.GET_INDIVIDUAL_RECORD) ||
               recordLoading.includes(RecordActionTypes.GET_CORPORATE_RECORD) ||
               screeningLoading.includes(SearchActionTypes.POST_SCREENING);
      })
    );
  }

  /**
   * Dispatch the effect to check search engine credit and proceed accordingly
   * @method checkBalance
   */
  checkBalance(): void {
    this.store.dispatch(new RootActions.billable.CheckBalance(<CheckBalancePayload>{
      actions: [
        new RootActions.search.PostScreening(<PostScreeningRequest>{
          generateReport: true,
          invalidOnly: true,
          redirectToScreening: true
        })
      ]
    }));
  }

  /**
   * Init customer for document wrapper component
   * @method initCustomer
   */
  initCustomer(): void {
    combineLatest([
      merge(
        this.store.select(Selectors.record.getCorporateRecord),
        this.store.select(Selectors.record.getIndividualRecord)
      ),
      this.store.select(Selectors.customer.getCustomer)
    ]).pipe(
      takeUntil(this.unsubscribe$),
    ).subscribe(
      ([record, customer]: [IndividualRecord | CorporateRecord, Customer]) => {
        if (record && customer) {
          this.customer = { record, customer };
        }
        this.cdRef.detectChanges();
      }
    );

    this.store.select(Selectors.customer.getCrps)
      .pipe(
        filter(crps => crps.results.length > 0),
        takeUntil(this.unsubscribe$)
      ).subscribe(
        (crps: CrpResponse) => {
          const primaryCrp = crps.results.find((crp: Crp) => crp.primary === true);

          if (primaryCrp && primaryCrp.recordType === 'INDIVIDUAL') {
            this.store.dispatch(new RootActions.record.GetIndividualRecord(<GetRecordPayload>{
              recordId: primaryCrp.record,
              getOtherData: true
            }));
          } else if (primaryCrp && primaryCrp.recordType === 'CORPORATE') {
            this.store.dispatch(new RootActions.record.GetCorporateRecord(<GetRecordPayload>{
              recordId: primaryCrp.record,
              getOtherData: true
            }));
          }
        }
      );

    this.store.select(Selectors.record.getRecordOtherData)
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe(
        (res: RecordOtherDataResponse) => {
          if (this.hasValidOtherData(res)) {
            this.otherData = res;
          } else {
            this.otherData = null;
          }
        }
      );
  }
/**
* Adds form in modal window for adding message to require update
* @method openRequireUpdateForm
*/
  openRequireUpdateForm(): void {
    this.requireUpdateFormRef = this.dialog.open(this.requireUpdateForm, {
      width: '650px'
    });
  }
/**
* Closes modal for adding adding message to require update and clearing the text inside
* @method closeRequireUpdateModal
*/
  closeRequireUpdateModal(): void {
    this.requireUpdateContent = '';
    this.requireUpdateFormRef.close();
  }

  requireUpdate(): void {
    const payload: PatchCustomerPayload = {
      customer: Object.assign({}, this.customer.customer),
      redirectToCustomerList: false,
      quietUpdate: true
    };

    payload.customer.integrationStatus = 'REQUIRE UPDATE';
    payload.customer.callback_message = this.requireUpdateContent;
    this.store.dispatch(new RootActions.customer.PatchCustomer(payload));
    this.closeRequireUpdateModal();
    this.cdRef.detectChanges();
  }

  /**
   * Inits observables for getting update domain action status
   * @method getRequireUpdateActionStatus
   */
  private getRequireUpdateActionStatus(): void {
    this.actions$.pipe(
      ofType<CustomerActions.PatchCustomerSuccess>(CustomerActions.CustomerActionTypes.PATCH_CUSTOMER_SUCCESS),
      takeUntil(this.unsubscribe$)
    )
    .subscribe(
      () => {
        this.snackbar.success('Update request successfully sent');
      }
    );

    this.actions$.pipe(
      ofType<CustomerActions.PatchCustomerFailure>(CustomerActions.CustomerActionTypes.PATCH_CUSTOMER_FAILURE),
      takeUntil(this.unsubscribe$)
    )
    .subscribe(
      (res: CustomerActions.PatchCustomerFailure) => {
        this.snackbar.error(res.payload);
        this.store.dispatch(new RootActions.customer.ClearErrors());
      }
    );
  }


  toEditPage(crpId: number, recordId: number): void {
    this.router.navigateByUrl(
      `customers/${this.customerID}/edit`
    );
  }

  addCRP(): void {
    this.router.navigate([`/customers/${this.customer.customer.id}/edit/crp-add`], { fragment: 'addCrpCard' });
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
